import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import DocumentsModern from 'src/components/Documents/DocumentsModern';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import LoadState from 'src/components/LoadState/LoadState';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';

interface Asset {
  id: string;
  name: string;
}

interface AssetDocumentsProps {
  asset: Asset;
  documents: any[];
  loadingDocs: boolean;
  isSaving?: boolean;
  isDeleting: boolean;
  saveDocument: (
    document: DocumentFormData,
    assetId: string,
    userId: string
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<boolean>;
}

const AssetDocuments = ({
  asset,
  documents,
  loadingDocs,
  isSaving = false,
  isDeleting,
  saveDocument,
  deleteDocument,
}: AssetDocumentsProps) => {
  const user = useSelector(selectAuthenticatedUser);

  const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);

  const onSave = async (document: DocumentFormData): Promise<void> => {
    await saveDocument(document, asset.id, user.id);
  };

  const onDeleteRequest = (document: any): void => {
    setDocumentToDelete(document);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!documentToDelete) {
      return;
    }

    await deleteDocument(documentToDelete.id);
    setDocumentToDelete(null);
  };

  const cancelDelete = (): void => setDocumentToDelete(null);

  return (
    <LoadState isLoading={loadingDocs}>
      {documentToDelete && (
        <ConfirmationModal
          title="Delete Document"
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{documentToDelete.name}</strong>?
            </>
          }
          warningMessage={
            <>
              <strong>Warning:</strong> deleting this document will delete this
              from the catalog and all other units and associated projects.
            </>
          }
          confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
          cancelLabel="Go Back"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          variant="danger"
        />
      )}
      <DocumentsModern
        documents={documents}
        user={user}
        labelName="Manuals & Documents"
        saving={isSaving}
        onSave={onSave}
        onDeleteDocument={onDeleteRequest}
        from="Asset"
        useCustomConfirmation
      />
    </LoadState>
  );
};

export default AssetDocuments;
